/**
 * Submit / edit community recipe — mockup-aligned form panel.
 */
import React, { useState, useRef, useEffect } from 'react';
import { FaPlus, FaPaperPlane, FaImage } from 'react-icons/fa';
import { communityRecipesService } from '../services/communityRecipesService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import {
  RECIPE_MAIN_PROTEINS,
  RECIPE_MAIN_VEGETABLES,
  RECIPE_INGREDIENT_UNITS,
  MAX_RECIPE_PHOTOS,
} from '../constants/communityRecipes';
import {
  emptyIngredient,
  reconcileRecipeIngredients,
  stripIngredientSyncKeys,
  hydrateIngredientsForEdit,
} from '../utils/syncRecipeMainIngredients';
import './camp-recipes-shell.css';

export function CommunityRecipeFormPanel({ editId = null, onClose, onSuccess }) {
  const isEdit = editId != null;
  const { currentUser, loading: userLoading } = useCurrentUser();
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingRecipe, setLoadingRecipe] = useState(Boolean(isEdit));
  const [error, setError] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    instructions: '',
    main_protein: 'none',
    main_vegetables: [],
    ingredients: [],
  });

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingRecipe(false);
      return undefined;
    }
    if (userLoading) return undefined;
    let cancelled = false;
    setLoadingRecipe(true);
    communityRecipesService
      .get(editId)
      .then((data) => {
        if (cancelled) return;
        if (data.source !== 'community' || Number(data.author_id) !== Number(currentUser?.id)) {
          setError('You can only edit your own community recipes.');
          setLoadingRecipe(false);
          return;
        }
        setForm({
          title: data.title || '',
          description: data.description || '',
          instructions: data.instructions || '',
          main_protein: data.main_protein || 'none',
          main_vegetables: [...(data.main_vegetables || [])],
          ingredients: hydrateIngredientsForEdit(data),
        });
        setError('');
        setLoadingRecipe(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load recipe.');
          setLoadingRecipe(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, userLoading, currentUser?.id]);

  const setField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const onMainProteinChange = (e) => {
    const main_protein = e.target.value;
    setError('');
    setForm((prev) => ({
      ...prev,
      main_protein,
      ingredients: reconcileRecipeIngredients({
        main_protein,
        main_vegetables: prev.main_vegetables,
        ingredients: prev.ingredients,
      }),
    }));
  };

  const toggleVeg = (slug) => {
    setForm((prev) => {
      const next = new Set(prev.main_vegetables);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      const main_vegetables = Array.from(next);
      return {
        ...prev,
        main_vegetables,
        ingredients: reconcileRecipeIngredients({
          main_protein: prev.main_protein,
          main_vegetables,
          ingredients: prev.ingredients,
        }),
      };
    });
  };

  const setIngredient = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((row, i) =>
        i === index ? { ...row, [key]: value } : row,
      ),
    }));
  };

  const addIngredientRow = () => {
    setForm((prev) => ({ ...prev, ingredients: [...prev.ingredients, emptyIngredient()] }));
  };

  const handlePhotoPick = (e) => {
    const f = e.target.files?.[0];
    setPhotoFile(f && f.type.startsWith('image/') ? f : null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Recipe title is required.');
      return;
    }
    const lines = stripIngredientSyncKeys(form.ingredients)
      .map((row) => ({
        name: (row.name || '').trim(),
        quantity: (row.quantity || '').trim() || undefined,
        unit: (row.unit || '').trim() || undefined,
        note: (row.note || '').trim() || undefined,
      }))
      .filter((row) => row.name);
    if (lines.length < 1) {
      setError('Choose a game type or vegetable, or add at least one ingredient.');
      return;
    }
    if ((form.instructions || '').trim().length < 20) {
      setError('Instructions should be at least 20 characters.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        description: (form.description || '').trim(),
        instructions: form.instructions.trim(),
        main_protein: form.main_protein || 'none',
        main_vegetables: form.main_vegetables,
        ingredients: lines,
      };
      let saved;
      if (isEdit && editId) {
        saved = await communityRecipesService.update(editId, payload);
      } else {
        saved = await communityRecipesService.create(payload);
      }
      if (photoFile) {
        await communityRecipesService.uploadRecipePhoto(saved.id, photoFile);
      }
      onSuccess?.(saved.id);
    } catch (err) {
      const body = err?.response?.data;
      const msg =
        body?.detail ||
        body?.instructions?.[0] ||
        body?.title?.[0] ||
        err?.message ||
        'Could not save recipe.';
      setError(typeof msg === 'string' ? msg : 'Could not save recipe.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingRecipe || (isEdit && userLoading)) {
    return <p className="camp-recipes-page__loading">Loading recipe…</p>;
  }

  return (
    <form
      className="camp-recipe-submit"
      onSubmit={handleSubmit}
      data-testid="camp-recipe-submit-form"
    >
      <p className="camp-recipe-submit__intro">
        Recipes are shared with the community. Game type and vegetables power &ldquo;Help me cook&rdquo;
        matching. Optional photo (up to {MAX_RECIPE_PHOTOS}).
      </p>

      <div className="camp-recipe-submit__field">
        <label className="camp-recipe-submit__label" htmlFor="recipe-submit-title">
          Recipe title
        </label>
        <input
          id="recipe-submit-title"
          className="camp-recipe-submit__input"
          placeholder="e.g., Grandma's Venison Stew"
          value={form.title}
          onChange={setField('title')}
        />
      </div>

      <div className="camp-recipe-submit__row camp-recipe-submit__row--2">
        <div className="camp-recipe-submit__field">
          <label className="camp-recipe-submit__label" htmlFor="recipe-submit-game">
            Game type
          </label>
          <select
            id="recipe-submit-game"
            className="camp-recipe-submit__select"
            value={form.main_protein}
            onChange={onMainProteinChange}
          >
            {RECIPE_MAIN_PROTEINS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="camp-recipe-submit__field">
          <label className="camp-recipe-submit__label" htmlFor="recipe-submit-difficulty">
            Difficulty
          </label>
          <select
            id="recipe-submit-difficulty"
            className="camp-recipe-submit__select camp-recipe-submit__select--disabled"
            disabled
            aria-disabled="true"
            title="Difficulty ratings — coming soon"
          >
            <option>Coming soon</option>
          </select>
        </div>
      </div>

      <div className="camp-recipe-submit__row camp-recipe-submit__row--3">
        {['Prep time (min)', 'Cook time (min)', 'Servings'].map((label) => (
          <div key={label} className="camp-recipe-submit__field">
            <label className="camp-recipe-submit__label">{label}</label>
            <input
              className="camp-recipe-submit__input"
              type="number"
              disabled
              aria-disabled="true"
              placeholder="—"
              title="Timing fields — coming soon"
            />
          </div>
        ))}
      </div>

      <div className="camp-recipe-submit__field">
        <label className="camp-recipe-submit__label" htmlFor="recipe-submit-desc">
          Description
        </label>
        <textarea
          id="recipe-submit-desc"
          className="camp-recipe-submit__textarea"
          rows={3}
          placeholder="A brief description of your recipe…"
          value={form.description}
          onChange={setField('description')}
        />
      </div>

      <div className="camp-recipe-submit__field">
        <span className="camp-recipe-submit__label">Main vegetables</span>
        <p className="camp-recipe-submit__hint">
          Select any you use — they are added to your ingredient list for amounts.
        </p>
        <div className="camp-recipe-submit__veg-chips" role="group" aria-label="Main vegetables">
          {RECIPE_MAIN_VEGETABLES.map((v) => {
            const on = form.main_vegetables.includes(v.value);
            return (
              <button
                key={v.value}
                type="button"
                className={`camp-recipe-submit__veg-chip${on ? ' camp-recipe-submit__veg-chip--on' : ''}`}
                aria-pressed={on}
                onClick={() => toggleVeg(v.value)}
              >
                {v.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="camp-recipe-submit__field">
        <span className="camp-recipe-submit__label">Ingredients</span>
        <p className="camp-recipe-submit__hint">
          Protein and vegetables appear automatically. Add amounts, then extras (salt, oil, stock).
        </p>
        {form.ingredients.length === 0 ? (
          <p className="camp-recipe-submit__hint camp-recipe-submit__hint--italic">
            Pick a game type or vegetable above, or add a row below.
          </p>
        ) : null}
        <ul className="camp-recipe-submit__ingredient-list">
          {form.ingredients.map((row, i) => (
            <li key={row._syncKey || `manual-${i}`} className="camp-recipe-submit__ingredient-row">
              <input
                className="camp-recipe-submit__input camp-recipe-submit__input--sm"
                placeholder="Name *"
                value={row.name}
                readOnly={Boolean(row._syncKey)}
                title={row._syncKey ? 'From game type or vegetables above' : undefined}
                onChange={(e) => setIngredient(i, 'name', e.target.value)}
              />
              <input
                className="camp-recipe-submit__input camp-recipe-submit__input--sm"
                placeholder="Qty"
                value={row.quantity}
                onChange={(e) => setIngredient(i, 'quantity', e.target.value)}
              />
              <select
                className="camp-recipe-submit__select camp-recipe-submit__select--sm"
                aria-label="Unit"
                value={row.unit || ''}
                onChange={(e) => setIngredient(i, 'unit', e.target.value)}
              >
                <option value="">Unit</option>
                {RECIPE_INGREDIENT_UNITS.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
              <input
                className="camp-recipe-submit__input camp-recipe-submit__input--sm"
                placeholder="Note"
                value={row.note}
                onChange={(e) => setIngredient(i, 'note', e.target.value)}
              />
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="camp-recipe-submit__add-row"
          onClick={addIngredientRow}
        >
          <FaPlus aria-hidden="true" />
          Add ingredient row
        </button>
      </div>

      <div className="camp-recipe-submit__field">
        <label className="camp-recipe-submit__label" htmlFor="recipe-submit-inst">
          Instructions
        </label>
        <textarea
          id="recipe-submit-inst"
          className="camp-recipe-submit__textarea camp-recipe-submit__textarea--tall"
          rows={5}
          placeholder="Step-by-step instructions…"
          value={form.instructions}
          onChange={setField('instructions')}
        />
      </div>

      <div className="camp-recipe-submit__field">
        <label className="camp-recipe-submit__label" htmlFor="recipe-submit-tags">
          Tags
        </label>
        <input
          id="recipe-submit-tags"
          className="camp-recipe-submit__input camp-recipe-submit__input--disabled"
          disabled
          aria-disabled="true"
          placeholder="e.g., slow-cooker, comfort-food (coming soon)"
          title="Recipe tags — coming soon"
        />
      </div>

      <div className="camp-recipe-submit__field">
        <span className="camp-recipe-submit__label">Photo</span>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="camp-recipe-submit__file-input"
          onChange={handlePhotoPick}
          aria-label="Upload recipe photo"
        />
        <button
          type="button"
          className="camp-recipe-submit__photo-drop"
          onClick={() => fileInputRef.current?.click()}
        >
          {photoPreview ? (
            <img src={photoPreview} alt="" className="camp-recipe-submit__photo-preview" />
          ) : (
            <>
              <span className="camp-recipe-submit__photo-icon" aria-hidden="true">
                <FaImage />
              </span>
              <span className="camp-recipe-submit__photo-text">Click to upload a photo</span>
              <span className="camp-recipe-submit__photo-hint">JPG, PNG, or WebP up to 10 MB</span>
            </>
          )}
        </button>
      </div>

      {error ? (
        <p className="camp-recipe-submit__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="camp-recipe-submit__footer">
        {onClose ? (
          <button
            type="button"
            className="camp-recipe-submit__btn camp-recipe-submit__btn--outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="submit"
          className="camp-recipe-submit__btn camp-recipe-submit__btn--primary"
          disabled={submitting}
        >
          <FaPaperPlane aria-hidden="true" />
          {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Submit recipe'}
        </button>
      </div>
    </form>
  );
}
